import { isWeightTotalComplete, sumWeights } from "@repo/shared";
import type { PortfolioCollectionListItem, PortfolioInCollection } from "@repo/shared/types";
import { useMemo } from "react";
import {
  useAddPortfolioProduct,
  useCollectionRiskScores,
  useCreatePortfolio,
  useDeletePortfolioProduct,
  usePlatforms,
  useProducts,
  useUpdatePortfolioProduct,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import {
  groupColumnsByScore,
  WeightsGrid,
  type WeightsGridColumn,
  type WeightsGridHandlers,
} from "../portfolio-weights";
import { T } from "./portfolios-grid.i18n";

type PortfoliosGridProps = {
  collection: PortfolioCollectionListItem;
};

export function PortfoliosGrid({ collection }: PortfoliosGridProps) {
  const localize = useLocalize();
  const t = useT(T);

  const { products } = useProducts(collection.id);
  const { riskScores: scores } = useCollectionRiskScores(collection.id);
  const { platforms } = usePlatforms(collection.id);
  const createPortfolio = useCreatePortfolio();
  const addPortfolioProduct = useAddPortfolioProduct();
  const updatePortfolioProduct = useUpdatePortfolioProduct();
  const deletePortfolioProduct = useDeletePortfolioProduct();

  const portfolios: PortfolioInCollection[] = collection.portfolios ?? [];

  // One column per portfolio, the platform variants of a risk score side by side.
  const columns = useMemo(
    () =>
      groupColumnsByScore(
        portfolios,
        platforms,
        localize,
        (portfolio, { platformNames, hasSiblings }): WeightsGridColumn => {
          const weights = portfolio.products.map((pp) => Number(pp.weight));
          const hasProducts = portfolio.products.length > 0;
          const name = localize(portfolio.nameI18n);
          const defaultName = `${t.riskNamePrefix} ${portfolio.riskScore}`;
          const customName = name && name !== defaultName ? name : null;
          // Portfolios sharing a risk score and the default name differ by platform only.
          const platformLabel = platformNames.length > 0 ? platformNames.join(", ") : t.noPlatform;
          return {
            id: portfolio.id,
            cells: Object.fromEntries(
              portfolio.products.map((pp) => [
                pp.product.id,
                { rowId: pp.id, weight: Number(pp.weight) },
              ]),
            ),
            total: sumWeights(weights),
            hasProducts,
            isIncomplete: hasProducts && !isWeightTotalComplete(weights),
            inlineName: customName ?? (hasSiblings ? platformLabel : null),
            tooltipTitle: name || defaultName,
            tooltipDescription: portfolio.descriptionI18n
              ? localize(portfolio.descriptionI18n) || null
              : null,
          };
        },
      ),
    [portfolios, platforms, localize, t.riskNamePrefix, t.noPlatform],
  );

  const handlers: WeightsGridHandlers = {
    addCell: (portfolioId, productId, weight) =>
      addPortfolioProduct.mutateAsync({
        collectionId: collection.id,
        portfolioId,
        data: { productId, weight },
      }),
    updateCell: (portfolioId, ppId, weight) =>
      updatePortfolioProduct.mutateAsync({
        collectionId: collection.id,
        portfolioId,
        ppId,
        data: { weight },
      }),
    deleteCell: (portfolioId, ppId) =>
      deletePortfolioProduct.mutateAsync({ collectionId: collection.id, portfolioId, ppId }),
    createColumn: async (score) => {
      const created = await createPortfolio.mutateAsync({
        collectionId: collection.id,
        data: {
          riskScore: score,
          nameI18n: {
            en_GB: `${T.en_GB.riskNamePrefix} ${score}`,
            nb_NO: `${T.nb_NO.riskNamePrefix} ${score}`,
          },
        },
      });
      return created.id;
    },
  };

  return (
    <WeightsGrid
      title={t.gridTitle}
      description={t.gridDescription}
      scores={scores}
      products={products.map((product) => ({
        id: product.id,
        name: localize(product.nameI18n),
        isin: product.isin,
      }))}
      columns={columns}
      emptyColumnTitle={(score) => `${t.riskNamePrefix} ${score}`}
      weightStep="0.01"
      handlers={handlers}
    />
  );
}
